import React, { useEffect, useState, useRef } from 'react';
import Banner from 'src/components/banner/Banner';
import { GlyphClose, IconInfo } from '@americanexpress/dls-icons';
import LiteralsService from 'src/services/literals/LiteralsService';
import { DLSSize } from 'src/types/dls-size.enum';
import styles from './categoryOverSpendingWarningBanner.module.scss';
import { useBudget } from 'src/hooks/queries/budgets';
import BudgetService, { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import IconButton from 'src/components/icon-button/IconButton';
import { ButtonType } from 'src/types/button-type.enum';
import { useFormikContext } from 'formik';
import { BudgetCreateFormValuesModel } from 'src/types/form-models/budget-create-form-values.model';
import Selectors from 'src/utils/selectors';
import { useHistory } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import { trackClick } from 'src/hooks/useAdobeTags';

/* There are 3 states for this banner:
 * 1. hide - Banner is hidden. This is the default state.
 * 2. show - Banner is shown unless the user has dismissed it.
 * 3. dismiss - Banner is dismissed by the user on pressing the close button and is no longer present. The banner will
 *  not be shown again for the current session.
 */

enum CategoryOverSpendingWarningBannerStates {
    HIDE,
    SHOW,
    DISMISS
}

export default function CategoryOverSpendingWarningBanner(): JSX.Element {
    const history = useHistory();
    const [showCategoryOverSpendingWarningBanner, setShowCategoryOverSpendingWarningBanner] =
        useState<CategoryOverSpendingWarningBannerStates>(CategoryOverSpendingWarningBannerStates.HIDE);
    const { isLoading: isLoadingTargetMonthlySpending, data: targetMonthlySpending } = useBudget(BUDGET_OVERALL_ID);
    const { values, touched } = useFormikContext<BudgetCreateFormValuesModel>();
    const bannerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        /* Scroll to banner if it is shown and user has interacted with the form. Do not scroll if the page has
         just loaded */
        if (showCategoryOverSpendingWarningBanner === CategoryOverSpendingWarningBannerStates.SHOW && touched.budgets) {
            bannerRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showCategoryOverSpendingWarningBanner]);

    const parentCategoryTotal: number = BudgetService.getAllParentCategoriesTotal(values.budgets);
    const targetMonthlySpendingAmount: number = targetMonthlySpending?.monthlyAmount;

    /* If total budget of all parent categories is greater than target monthly spending amount and banner
     was hidden before, show banner */
    if (
        parentCategoryTotal > targetMonthlySpendingAmount &&
        showCategoryOverSpendingWarningBanner === CategoryOverSpendingWarningBannerStates.HIDE
    ) {
        setShowCategoryOverSpendingWarningBanner(CategoryOverSpendingWarningBannerStates.SHOW);
    } /* If total budget of all parent categories is less than or equal to target monthly spending amount
     and banner is shown, hide banner */ else if (
        parentCategoryTotal <= targetMonthlySpendingAmount &&
        showCategoryOverSpendingWarningBanner === CategoryOverSpendingWarningBannerStates.SHOW
    ) {
        setShowCategoryOverSpendingWarningBanner(CategoryOverSpendingWarningBannerStates.HIDE);
    }

    /* Dismiss banner and do not show it again for the current user session */
    const onCloseBanner = (): void => {
        trackClick(history.location.pathname, adobeTags.Banner.budgetExceedingTargetCancel);
        setShowCategoryOverSpendingWarningBanner(CategoryOverSpendingWarningBannerStates.DISMISS);
    };

    if (
        isLoadingTargetMonthlySpending ||
        showCategoryOverSpendingWarningBanner !== CategoryOverSpendingWarningBannerStates.SHOW
    )
        return null;

    return (
        <Banner
            icon={<IconInfo size={DLSSize.MEDIUM} />}
            bannerRef={bannerRef}
            className={styles.banner}
            text={LiteralsService.getLiteral('Budget.Create.categoryOverSpendingWarningBanner.description')}
            data-cy={Selectors.SpendSmart.CATEGORY_OVER_SPENDING_WARNING_BANNER}
        >
            <IconButton
                variant={ButtonType.TERTIARY}
                icon={<GlyphClose size={DLSSize.MEDIUM} />}
                title={LiteralsService.getLiteral('Budget.Create.categoryOverSpendingWarningBanner.close')}
                onClick={onCloseBanner}
                className={styles.closeButton}
                data-cy={Selectors.SpendSmart.CLOSE_BUTTON}
            />
        </Banner>
    );
}
